/**
 * Tag Recovery (04 §2.3): when a mission carries `dogTags`, the fallen ranger's marker must be a
 * real detour and a fair one - a `fieldGrave` in the approaches or the belt, off the roads, clear
 * of mines, wire and everything solid, that can be walked to from the drop band without crossing
 * a mine, 25-60 m off the straight line from the start to the goal.
 */

import { roadDistance } from '../../sim/statics';
import type { MissionDef, Rect } from '../../sim/types';
import { TAGS } from '../tuning';
import type { Band } from '../types';
import type { NavProbe } from './nav';

/** How far a point lies off the straight line from the ranger's start to the middle of the goal zone. */
export function detourOf(player: { x: number; z: number }, goalZone: Rect, x: number, z: number): number {
  const gx = (goalZone.x0 + goalZone.x1) / 2;
  const gz = (goalZone.z0 + goalZone.z1) / 2;
  const vx = gx - player.x;
  const vz = gz - player.z;
  const len2 = vx * vx + vz * vz;
  const t = len2 === 0 ? 0 : Math.min(1, Math.max(0, ((x - player.x) * vx + (z - player.z) * vz) / len2));
  const dx = x - (player.x + vx * t);
  const dz = z - (player.z + vz * t);
  return Math.sqrt(dx * dx + dz * dz);
}

export function checkDogTags(def: MissionDef, nav: NavProbe, bands: readonly Band[]): string[] {
  const tags = def.dogTags;
  if (!tags) return [];
  const out: string[] = [];
  if (tags.name.trim() === '') out.push('the dog tags belong to nobody: the name is empty');
  const grave = def.props.find((p) => p.id === tags.propId);
  if (!grave || grave.kind !== 'fieldGrave') {
    return [...out, `the dog tags are at prop ${tags.propId}, which is not a field grave`];
  }
  if (def.props.filter((p) => p.kind === 'fieldGrave').length !== 1) out.push('there must be exactly one field grave');

  const where = `the field grave at ${grave.x}, ${grave.z}`;
  const band = bands.find((b) => grave.z >= b.z0 && grave.z < b.z1);
  const inBand = band?.kind === 'approach' || band?.kind === 'belt';
  if (!inBand) out.push(`${where} must stand in an approach band or the belt`);
  if (roadDistance(def, grave.x, grave.z) <= 0) out.push(`${where} stands on a road`);
  const cx = Math.floor(grave.x);
  const cz = Math.floor(grave.z);
  const cell = cx < 0 || cz < 0 || cx >= nav.width || cz >= nav.height ? -1 : cz * nav.width + cx;
  if (cell < 0) return [...out, `${where} is off the map`];
  if (nav.soft[cell] !== 0) out.push(`${where} stands in a minefield or in wire`);
  else if (nav.full.blocked[cell] !== 0) out.push(`${where} stands inside something solid, or in water`);
  // The pickup's mine-free component holds (at least) 80 % of the drop band: the start must be part of it too.
  const start = Math.floor(def.player.z) * nav.width + Math.floor(def.player.x);
  if (nav.cost[cell] !== 0 || nav.cost[start] !== 0) {
    out.push(`${where} cannot be walked to from the drop band without crossing mines or wire`);
  }
  const off = detourOf(def.player, def.goalZone, grave.x, grave.z);
  const [near, far] = TAGS.detour;
  if (off < near || off > far) {
    out.push(`${where} is ${Math.round(off)} m off the direct line: it should be a detour of ${near}-${far} m`);
  }
  return out;
}
